import { axios } from '@/services/axios/config'
import { useUserStore } from '@/stores/user'
import { IBusinessRequest, IPublicBusinessRequest, IBusinessRequestImage, IBusinessRequestInterest } from '@/models/interfaces/interfaces'
import { getUserFromLocalStorage } from '@/utils/functions'
import { IUser } from '@/models/interfaces/interfaces'

/**
 * API function to fetch list of business request
 * @returns { Promise }
 */
export const getBusinessRequestList = async () => {
    const userStore = useUserStore()
    const user = userStore.getUser

    let payload = {
        "client_id": user.party_id,
        "status_list":
            [
                "g_lst_gen_status_pending",
                "g_lst_gen_status_active",
                "g_lst_gen_status_inactive",
                "g_lst_gen_status_archived",
            ],
        "paginated": false
    }

    const config = {
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${user.token.access}`
        }
    };

    let response = await axios.post('/quotes/business_request_list/', payload, config)
    return response.data as IBusinessRequest[]
}

/**
 * API function to fetch list of public business requests
 * @returns { Promise<IPublicBusinessRequest[]> }
 */
export const getPublicBusinessRequestList = async () => {
    const response = await axios.get('/quotes/business_request_list_public/')
    const items = Array.isArray(response?.data?.items) ? response.data.items : []
    return items.slice(0, 15) as IPublicBusinessRequest[]
}

/**
 * API function to fetch business request details
 * @returns { Promise }
 */
export const getBusinessrequestDetail = async (id: number) => {
    const userStore = useUserStore()
    const user = userStore.getUser

    const config = {
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${user.token.access}`
        }
    };

    const payload = {
        client_id: user.party_id,
        business_request_id: id,
    }

    let response = await axios.post('/quotes/business_request_details/', payload, config)
    return response.data as IBusinessRequest
}

/**
 * API function to create or modify a business request 
 * @returns { Promise }
 */
export const createOrUpdateBusinessrequest = async (payload: any, images: Blob[] | File[] | null = null) => {
    const userStore = useUserStore()
    let user = userStore.getUser

    if (!user) {
        user = getUserFromLocalStorage() as IUser
    }

    const config = {
        headers: {
            "Content-Type": "multipart/form-data",
            "Authorization": `Bearer ${user.token.access}`
        }
    };

    const formData = new FormData();

    // Populate FormData dynamically
    Object.entries(payload).forEach(([key, value]) => {
        if (Array.isArray(value)) {
            // If the value is an array, append each item individually
            value.forEach(item => formData.append(key, item));
        } else {
            // Otherwise, append the value directly
            formData.append(key, value);
        }
    });

    if (images && images.length > 0) {
        for (let i = 0; i < images.length; i++) {
            formData.append('images[]', images[i]);
        }
    }

    if (!payload.id) {
        // create with POST
        let response = await axios.post('/quotes/business_request/', formData, config)
        return response.data as IBusinessRequest
    } else {
        // update with PUT
        let response = await axios.put('/quotes/business_request/', formData, config)
        return response.data as IBusinessRequest
    }
}

/**
 * API function to upload business request image
 * @returns { Promise }
 */
export const uploadBusinessrequestImages = async (business_request_id: number, images: Blob[] | File[] | null) => {
    const userStore = useUserStore()
    const user = userStore.getUser

    const config = {
        headers: {
            "Content-Type": "multipart/form-data",
            "Authorization": `Bearer ${user.token.access}`
        }
    };

    const formData = new FormData();
    formData.append('client_id', user.party_id + "");
    formData.append('business_request_id', business_request_id + "");

    if (images && images.length > 0) {
        for (let i = 0; i < images.length; i++) {
            formData.append('images[]', images[i]);
        }
    }

    let response = await axios.post('/quotes/business_request_image/', formData, config);
    return response.data as IBusinessRequestImage[];

}

/**
 * API function to delete business request images
 * @returns { Promise }
 */
export const deleteBusinessrequestImages = async (business_request_id: number, images_ids: any[]) => {
    const userStore = useUserStore()
    const user = userStore.getUser

    const payload = {
        client_id: user.party_id,
        business_request_id: business_request_id,
        images_ids: images_ids
    }

    const config = {
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${user.token.access}`
        },
        data: payload,
    };


    let response = await axios.delete('/quotes/business_request_image/', config)
    return response.data as IBusinessRequest
}

/**
 * #retraction
 * API function to perform a client action on a business request interest
 * @returns { Promise } updated list of business_request_interests
 */
export const businessRequestClientAction = async (business_request_id: number, business_request_interest_id: number, action: string) => {
    const userStore = useUserStore()
    const user = userStore.getUser
 
    const config = {
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${user.token.access}`
        }
    };
 
    const payload = {
        client_id: user.party_id,
        business_request_id,
        business_request_interest_id,
        action,
    }
 
    let response = await axios.put('/quotes/business_request_client_actions/', payload, config)
    return response.data as IBusinessRequestInterest[]
}

/**
 * API function to an open ai description based on the title
 * @returns { Promise }
 */
export const getDescriptionFromOpenAI = async (payload: any) => {
    const config = {
        headers: {
            "Content-Type": "application/json",
        },
        timeout: 5000
    };

    let response = await axios.post('/quotes/business_request_ai_helper/', payload, config)
    return response.data

}
